import { Box } from '@mui/material';
import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector, useAuth } from '@verantos-dev/core-annotation-tool';
import {
  DEFAULT_LEFT_SIDEBAR_WIDTH,
  DEFAULT_RIGHT_SIDEBAR_WIDTH,
} from '@verantos-dev/core-annotation-tool';
import { useFeatureFlagSplit } from '@verantos-dev/core-utils';
import { selectBatchId } from '@verantos-dev/feature-supervisor-queue';
import { setBatchId } from '@verantos-dev/feature-supervisor-queue';
import { LoadingSpinner } from '@verantos-dev/shared-components';
import { useResizeHandle } from '@verantos-dev/ui-annotation-tool';
import { ResizeHandle } from '@verantos-dev/ui-annotation-tool';

import { useGetSupervisorWorkspaceQuery } from './api/supervisorApiSlice';
import { NoteListSidebarSupervisor } from './components/NoteListSidebarSupervisor/NoteListSidebarSupervisor';
import { ReviewAnnotationsSidebar } from './components/ReviewAnnotationsSidebar/ReviewAnnotationsSidebar';
import { SupervisorContent } from './components/SupervisorContent/SupervisorContent';
import { useSupervisorMode } from './context/SupervisorModeContext';
import { selectShowAnnotations } from './state/selector';

export const SupervisorScreen = () => {
  const {
    startLeftResize,
    startRightResize,
    rightWidth,
    leftWidth,
    setRightInitialWidthToDefault,
    setLeftInitialWidthToDefault,
  } = useResizeHandle({
    initialLeftWidth: DEFAULT_LEFT_SIDEBAR_WIDTH,
    initialRightWidth: DEFAULT_RIGHT_SIDEBAR_WIDTH,
    minRightWidth: DEFAULT_RIGHT_SIDEBAR_WIDTH,
  });
  const showAnnotations = useAppSelector(selectShowAnnotations);

  // batchId is passed via the ?batchId= query param (see useSupervisorQueueContent) so it
  // survives a page refresh, which resets the supervisorQueue Redux slice to null. Re-sync
  // it into Redux here, and hold off mounting children until it's in sync, so the workspace
  // query hooks below (which read batchId from Redux) never fire with a stale/null batchId.
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const batchId = useAppSelector(selectBatchId);
  const batchIdFromUrl = searchParams.get('batchId');

  useEffect(() => {
    if (batchIdFromUrl && batchIdFromUrl !== batchId) {
      dispatch(setBatchId(batchIdFromUrl));
    }
  }, [batchIdFromUrl, batchId, dispatch]);

  const { encounterId } = useParams();
  const { user } = useAuth();
  const { isReadOnly } = useSupervisorMode();
  // Re-invokes the same query the panels' own hooks call (useSupervisorContent,
  // useNoteListSidebarSupervisor, useSupervisorReviewTabSection, useConflictTab,
  // useAnnotatorAnnotationsTab, useResolvedTab) purely to read its loading flags here.
  // RTK Query dedupes the request via its cache, so this doesn't trigger an extra fetch.
  // Also skipped while the batchId query-param sync above hasn't completed, so this never
  // fires with a stale/null batchId.
  const { isLoading: isSupervisorWorkspaceLoading, isFetching: isSupervisorWorkspaceFetching } =
    useGetSupervisorWorkspaceQuery(
      { encounterId: encounterId as string, batchId: batchId as string },
      { skip: (!user.isUser && !isReadOnly) || (!!batchIdFromUrl && batchIdFromUrl !== batchId) }
    );
  // TODO: cleanup with cp_annotation_show_dashboard_loading_spinner ff
  const showLoadingSpinner = useFeatureFlagSplit(
    'cp_annotation_show_dashboard_loading_spinner'
  ) as boolean;
  const isLoading = isSupervisorWorkspaceLoading || isSupervisorWorkspaceFetching;

  if (batchIdFromUrl && batchIdFromUrl !== batchId) {
    return null;
  }

  return showLoadingSpinner && isLoading ? (
    <Box
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}
    >
      <LoadingSpinner id="supervisorScreenLoading" />
    </Box>
  ) : (
    <Box sx={{ display: 'flex' }}>
      <NoteListSidebarSupervisor leftWidth={leftWidth} />
      <ResizeHandle onMouseDown={(e: React.MouseEvent) => startLeftResize(e)} />
      <SupervisorContent />
      {showAnnotations && (
        <ResizeHandle onMouseDown={(e: React.MouseEvent) => startRightResize(e)} />
      )}
      <ReviewAnnotationsSidebar rightWidth={rightWidth} />
    </Box>
  );
};
